import { useState, useEffect } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { Box, Breadcrumbs } from "@mui/material";
import Grid from "@mui/material/Grid";
import GridTable from "../../../components/GridTable";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BusinessIcon from "@mui/icons-material/Business";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import TextField from "@mui/material/TextField";
import Dialog from "../../../components/Dialog";
import Loader from "../../../components/Loader";
import Button from "../../../components/Button";
import ActionMenu from "../../../components/ActionMenu";
import { successMessage, errorMessage } from "../../../components/Toaster";
import { getYardsByCompanyId, createYard, updateYard, deleteYard } from "./api";
import { getErrorMessage } from "../../../utils/error";

const Yard = () => {
  const location = useLocation();
  const { company_id } = useParams();
  const navigate = useNavigate();
  const company_name =
    location.state?.company_name ||
    localStorage.getItem("company_name") ||
    "Company";
  const [yards, setYards] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState({
    fetch: false,
    create: false,
    update: false,
    delete: false,
  });
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [editYard, setEditYard] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const validationSchema = Yup.object().shape({
    yardName: Yup.string().required("Yard Name is required"),
  });

  const columns = [
    {
      field: "yard_name",
      headerName: "Yard Name",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerClassName: "table-header",
      renderCell: (params) => (
        <ActionMenu
          params={params}
          actions={[
            {
              icon: <EditIcon color="primary" />,
              tooltip: "Edit",
              onClick: handleEdit,
            },
            {
              icon: <DeleteIcon color="error" />,
              tooltip: "Delete",
              onClick: handleDelete,
            },
            {
              icon: <AccountCircleIcon color="action" />,
              tooltip: "Users",
              onClick: handleViewUsers,
            },
            {
              icon: <Inventory2Icon color="success" />,
              tooltip: "Machines",
              onClick: handleViewMachines,
            },
          ]}
        />
      ),
      flex: 1,
    },
  ];

  useEffect(() => {
    const fetchYards = async () => {
      setLoading((p) => ({ ...p, fetch: true }));
      try {
        const res = await getYardsByCompanyId(company_id || "");
        if (res.status) {
          setYards(res.data);
        } else {
          errorMessage(res.message || "Failed to fetch yards");
        }
      } catch (err) {
        errorMessage(getErrorMessage(err) || "Error fetching yards");
      } finally {
        setLoading((p) => ({ ...p, fetch: false }));
      }
    };

    if (company_id) fetchYards();
  }, [company_id]);

  const openDialog = () => setDialogOpen(true);

  const closeDialog = (resetForm) => {
    resetForm();
    setDialogOpen(false);
    setEditYard(null);
  };

  const handleEdit = (params) => {
    setEditYard(params.row);
    openDialog();
  };

  const handleDelete = (params) => {
    setItemToDelete(params);
    setDeleteOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteOpen(false);
    setItemToDelete(null);
  };

  const handleViewUsers = (params) => {
    const yard_id = params.row.yard_id;
    const yard_name = params.row.yard_name;
    navigate(`/company/${company_id}/yards/${yard_id}/users`, {
      state: { company_name, yard_name },
    });
  };

  const handleViewMachines = (params) => {
    const yard_id = params.row.yard_id;
    const yard_name = params.row.yard_name;
    navigate(`/company/${company_id}/yards/${yard_id}/machines`, {
      state: { company_name, yard_name },
    });
  };

  const confirmDialog = async (values, { resetForm }) => {
    const success = editYard
      ? await handleUpdate(values)
      : await handleCreate(values);

    if (success) closeDialog(resetForm);
  };

  const handleCreate = async (values) => {
    setLoading((p) => ({ ...p, create: true }));
    try {
      const payload = {
        yard_name: values.yardName.trim(),
        company_id: company_id,
      };

      const res = await createYard(payload);
      if (res.status) {
        setYards((prev = []) => [
          ...(Array.isArray(prev) ? prev : []),
          { ...payload, yard_id: res.data.yard_id },
        ]);
        successMessage(res.message);
        return true;
      } else {
        errorMessage(res.message);
        return false;
      }
    } catch (err) {
      errorMessage(getErrorMessage(err));
      return false;
    } finally {
      setLoading((p) => ({ ...p, create: false }));
    }
  };

  const handleUpdate = async (values) => {
    setLoading((p) => ({ ...p, update: true }));
    try {
      const payload = {
        yard_id: editYard.yard_id,
        yard_name: values.yardName.trim(),
        company_id: company_id,
      };

      const res = await updateYard(payload);
      if (res.status) {
        setYards((prev) =>
          prev.map((y) =>
            y.yard_id === editYard.yard_id
              ? { ...y, yard_name: values.yardName }
              : y,
          ),
        );
        successMessage(res.message);
        return true;
      } else {
        errorMessage(res.message);
        return false;
      }
    } catch (err) {
      errorMessage(getErrorMessage(err));
      return false;
    } finally {
      setLoading((p) => ({ ...p, update: false }));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    setLoading((p) => ({ ...p, delete: true }));
    try {
      const res = await deleteYard(itemToDelete.row.yard_id);
      if (res.status) {
        setYards((prev) =>
          prev.filter((y) => y.yard_id !== itemToDelete.row.yard_id),
        );
        successMessage(res.message);
      } else {
        errorMessage(res.message);
      }
    } catch (err) {
      errorMessage(getErrorMessage(err));
    } finally {
      setLoading((p) => ({ ...p, delete: false }));
      setDeleteOpen(false);
    }
  };

  return (
    <>
      {loading.fetch ? (
        <Loader color="primary" size={40} />
      ) : (
        <Box>
          <main className="m-a-50">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "30px",
              }}
            >
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                style={{ fontSize: "18px", fontWeight: "bold" }}
              >
                <Link color="primary" to="/company">
                  Company
                </Link>
                <Link color="primary" to={`/company`}>
                  {company_name}
                </Link>
                <Link
                  color="primary"
                  to={`/company/${company_id}/yards`}
                  state={{ company_name: company_name }}
                >
                  Yards
                </Link>
              </Breadcrumbs>
            </div>
            <div className="d-flex justify-end">
              <div className="d-flex justify-between w-100">
                <TextField
                  className="w-25"
                  label="Search"
                  variant="outlined"
                  value={searchQuery}
                  size="small"
                  startIcon={<SearchIcon />}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Button
                  label="Add"
                  variant="contained"
                  color="primary"
                  onClick={openDialog}
                />
              </div>
            </div>
            <div
              style={{ height: "100%", width: "100%" }}
              className="ag-theme-alpine m-t-30"
            >
              <GridTable
                rows={yards}
                columns={columns}
                pageSize={10}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                getRowId={(row) => row.yard_id}
                density="standard"
              />
            </div>
          </main>
        </Box>
      )}
      <Formik
        initialValues={{
          yardName: editYard ? editYard.yard_name : "",
        }}
        validationSchema={validationSchema}
        onSubmit={confirmDialog}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          resetForm,
        }) => (
          <>
            <Dialog
              open={isDialogOpen}
              onClose={() => closeDialog(resetForm)}
              onConfirm={handleSubmit}
              title={
                !loading.update && !loading.create
                  ? editYard
                    ? "Edit Yard"
                    : "Add New Yard"
                  : ""
              }
              confirmButtonText={
                !loading.update && !loading.create
                  ? editYard
                    ? "Update"
                    : "Add"
                  : ""
              }
              cancelButtonText={
                !loading.update && !loading.create ? "Cancel" : ""
              }
              icon={<BusinessIcon />}
              message={
                <Box padding={4}>
                  {loading.update || loading.create ? (
                    <Loader color="primary" size={40} />
                  ) : (
                    <Form>
                      <Grid container spacing={2}>
                        <Grid size={12}>
                          <TextField
                            id="yardName"
                            name="yardName"
                            label="Yard Name"
                            required
                            value={values.yardName}
                            onChange={handleChange}
                            error={touched.yardName && Boolean(errors.yardName)}
                            helperText={touched.yardName && errors.yardName}
                          />
                        </Grid>
                      </Grid>
                    </Form>
                  )}
                </Box>
              }
            />
          </>
        )}
      </Formik>
      <Dialog
        open={deleteOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={!loading.delete ? "Delete Yard" : ""}
        confirmButtonText={!loading.delete ? "Confirm" : ""}
        cancelButtonText={!loading.delete ? "Cancel" : ""}
        icon={<DeleteIcon />}
        message={
          <Box padding={2}>
            {loading.delete ? (
              <Loader color="primary" size={40} />
            ) : (
              "Are you sure you want to delete this yard?"
            )}
          </Box>
        }
      />
    </>
  );
};

export default Yard;
